/**
 * Accepts a string in object notation and returns the value from the supplied scope.
 *
 * @param {string} input the string to convert
 * @param {Object} scope an object with properties to use within the string
 * @param {boolean} debug Optional. If set to true will throw an error on missing variables
 * @returns {Object} The parsed value
 */
function parseObject(input, scope, debug = false) {
    const parts = input.trim().split(/\./g)
    let buffer = scope

    for (let i = 0, ilen = parts.length; i < ilen; ++i) {
        if (parts[i] === 'this') continue

        buffer = buffer[parts[i]]

        if (buffer === undefined) break
    }

    if (buffer === undefined && debug)
        throw new Error(`${ input } not found in scope`, scope)

    if (buffer === undefined)
        buffer = ''

    return buffer
}

/**
 * Functions for manipulating strings.
 */
export default {

    /**
     * Accept a string and replace text delimited by ${ and } with variables
     * from the scope object.
     *
     * @param {string} input the string to convert
     * @param {Object} scope an object with properties to use within the string
     * @param {boolean} debug Optional. If set to true will throw an error on missing variables
     * @returns {string} The parsed string
     */
    parse: function(input, scope, debug = false) {
        const regex = /\$\{([^}]+)}/g

        return input.replace(regex, function variableReplacer(_, word) {
            return parseObject(word, scope, debug)
        })
    },

    /**
     * Accept a string in the form a=1&b=2 and return an object with appropriate keys and values. Converts
     * multiple keys with the same name as an array so 'a=1&a=2' returns `{ a: [1, 2] }`. If `selectKey` is provided
     * only the value of that key is returned.
     *
     * @param {string} input the string to convert
     * @param {?string} selectKey the key to return
     * @returns {Object|any} The parsed object.
     */
    query: function(input, selectKey = null) {
        const querystring = {}

        if (!input) return {}

        input.split('&').forEach(items => {
            const [ key, value ] = items.split('=')

            // convert multiples of the same key into an array
            if (key in querystring) {
                if (!Array.isArray(querystring[key]))
                    querystring[key] = [ querystring[key] ]

                querystring[key].push(value)
            } else {
                querystring[key] = value
            }
        })

        return selectKey ? querystring[selectKey] : querystring
    },

    /**
     * Converts an object to query string parameters.
     * @param {Object} input
     * @returns {string}
     */
    toQuery: function(input) {
        if (!input)
            return ''

        const output = new URLSearchParams(input).toString()

        return output ? `?${ output }` : ''
    },

    parseObject,
}
